import React, { Fragment, useEffect, useMemo, useState } from "react";
import SubmenuLayout from "../../components/Layouts/SubmenuLayout";
import Container from "@material-ui/core/Container";
import { connect, useDispatch } from "react-redux";
import { Client } from "../../models/model";
import { createOptions, unwrap } from "../../helpers/utils";
import { updateClient } from "../../store/client/actions";
import { createPutSuccessMethod } from "reactcoregk/store/actions";
import ClientForm from "./ClientForm";
import Grid from "@material-ui/core/Grid";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import SecurityIcon from "@material-ui/icons/Security";
import SectionGroupDescription from "../../components/SectionGroupDescription";
import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/styles";
import Box from "@material-ui/core/Box";
import Alert from "@material-ui/lab/Alert";
import AlertTitle from "@material-ui/lab/AlertTitle";
import LicenseList from "./license-list";
import {
  Button,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { Save } from "@material-ui/icons";
import CreateLicenseDialog from "./create-license-dialog";
import { createLicense } from "../../store/license/actions";
import { EntityType } from "../../store/@core/entityType";
import { HiveIcons } from "../../assets/icons";
import ErrorBoundary from "../../components/ErrorBoundary";
import { useEntityProgress, useForm } from "reactcoregk/hooks";
import { Auth } from "../../models/Auth";
import { AUTHORITY } from "../../constants/enums";
import { updateData } from "../../helpers/utils";
import { API_URL } from "../../config";

const useStyles = makeStyles({
  paper: {
    padding: 16,
  },
});

// Backend defaults for new clients (KRON-7453)
const DEFAULT_HISTORICAL_RETENTION_DAYS = 180;
const DEFAULT_LIVE_RETENTION_DAYS = 365;

const isValidRetentionDays = (value) =>
  Number.isInteger(Number(value)) && Number(value) >= 1;

const ClientView = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { context, updateClient, createLicense } = props;
  const id = props.match.params.id;
  const [form, handleChange, setForm] = useForm(new Client());
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [busy, error] = useEntityProgress(context.Client, () => {});
  const loadingLicense = context.License.create.isLoading;
  const [openLicense, setOpenLicense] = useState(false);
  const [retainLiveDataBusy, setRetainLiveDataBusy] = useState(false);

  const canManageRetainLiveData =
    Auth.hasRole(AUTHORITY.ADMIN) ||
    Auth.hasRole(AUTHORITY.LICENSE_ADMIN) ||
    Auth.hasRole(`ROLE_${AUTHORITY.ADMIN}`) ||
    Auth.hasRole(`ROLE_${AUTHORITY.LICENSE_ADMIN}`);

  // retainLiveData: true = data kept indefinitely, i.e. retention policy OFF
  const retentionPolicyOn = !form.retainLiveData;

  const handleRetentionPolicyToggle = (e) => {
    const policyOn = e.target.checked;
    setForm((prev) => ({ ...prev, retainLiveData: !policyOn }));
  };

  const historicalDaysValid = isValidRetentionDays(
    form.historicalDataRetentionDays
  );
  const liveDaysValid = isValidRetentionDays(form.liveDataRetentionDays);
  const retentionValid = historicalDaysValid && liveDaysValid;

  const handleSaveRetentionSettings = () => {
    const payload = {
      id: parseInt(id),
      retainLiveData: Boolean(form.retainLiveData),
      historicalDataRetentionDays: parseInt(
        form.historicalDataRetentionDays,
        10
      ),
      liveDataRetentionDays: parseInt(form.liveDataRetentionDays, 10),
    };
    setRetainLiveDataBusy(true);
    updateData(`${API_URL}/api/clients/retain-live-data`, payload)
      .then(() => {
        if (client) {
          dispatch(
            createPutSuccessMethod(EntityType.Client, {
              ...client,
              retainLiveData: payload.retainLiveData,
              historicalDataRetentionDays: payload.historicalDataRetentionDays,
              liveDataRetentionDays: payload.liveDataRetentionDays,
            })
          );
        }
      })
      .catch((err) => setErrorMessage(err.message))
      .finally(() => setRetainLiveDataBusy(false));
  };

  useEffect(() => setErrorMessage(error), [error]);
  const verticals = context.CompanyVertical.getAll.result;
  const countries = context.Country.getAll.result;
  const versions = context.Version.getAll.result;
  const clients = context.Client.getAll.result;
  const isLoading = context.Client.getAll.isLoading;

  const verticalOptions = createOptions(verticals);
  const countryOptions = createOptions(countries);
  const versionOptions = createOptions(versions);

  const client = useMemo(
    () => clients.find((x) => x.id === parseInt(id)),
    [clients, id]
  );

  const retentionDirty =
    Boolean(client) &&
    (Boolean(form.retainLiveData) !== Boolean(client.retainLiveData) ||
      Number(form.historicalDataRetentionDays) !==
        (client.historicalDataRetentionDays ||
          DEFAULT_HISTORICAL_RETENTION_DAYS) ||
      Number(form.liveDataRetentionDays) !==
        (client.liveDataRetentionDays || DEFAULT_LIVE_RETENTION_DAYS));

  useEffect(() => {
    if (client)
      setForm({
        ...client,
        historicalDataRetentionDays:
          client.historicalDataRetentionDays ||
          DEFAULT_HISTORICAL_RETENTION_DAYS,
        liveDataRetentionDays:
          client.liveDataRetentionDays || DEFAULT_LIVE_RETENTION_DAYS,
      });
  }, [client, setForm]);

  return (
    <SubmenuLayout
      MenuItemsComponent={Fragment}
      title={unwrap(client, "name") || "New client"}
      backLink={"/clients"}
    >
      <Container maxWidth={false}>
        {!isLoading && !client && (
          <Alert severity="error" style={{ marginBottom: 16 }}>
            <AlertTitle>Error</AlertTitle>
            Could not found client with id <strong>{id}</strong>
          </Alert>
        )}
        {errorMessage && (
          <Alert
            severity="error"
            onClose={() => setErrorMessage(null)}
            style={{ marginBottom: 16 }}
          >
            {errorMessage}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item md={4} sm={12} xs={12}>
            <Paper variant={"outlined"} className={classes.paper}>
              <Grid container spacing={2}>
                <Grid item style={{ flex: 1 }}>
                  <SectionGroupDescription
                    title={"Client info"}
                    IconComponent={AssignmentIndIcon}
                  />
                </Grid>
                <Grid item>
                  <Button
                    style={{ marginLeft: "auto" }}
                    disabled={busy || !client}
                    color={"primary"}
                    variant={"outlined"}
                    onClick={() => updateClient(form)}
                  >
                    <Save />
                    Update info
                  </Button>
                </Grid>
              </Grid>

              <Box my={3} />
              <ClientForm
                disabled={busy || !client}
                form={form}
                handleChange={handleChange}
                verticalOptions={verticalOptions}
                countryOptions={countryOptions}
                versionOptions={versionOptions}
              />
              <Box mt={2}>
                <strong>Is SaaS:</strong> {form.onSaas ? "Yes" : "No"}
              </Box>
              {/* Data retention is not applicable for non-SaaS clients (Is SaaS: No) */}
              {Boolean(form.onSaas) && (
                <Box mt={3}>
                  <Paper variant={"outlined"} className={classes.paper}>
                    <Typography variant="subtitle2">
                      Data Retention Policy
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={retentionPolicyOn}
                          onChange={handleRetentionPolicyToggle}
                          color="primary"
                          disabled={
                            !client ||
                            retainLiveDataBusy ||
                            !canManageRetainLiveData
                          }
                        />
                      }
                    />
                    <TextField
                      variant="standard"
                      margin="normal"
                      fullWidth
                      type="number"
                      inputProps={{ min: 1 }}
                      label="Closed Vulnerabilities (Days)"
                      placeholder="Enter days"
                      value={form.historicalDataRetentionDays}
                      disabled={
                        !client ||
                        retainLiveDataBusy ||
                        !retentionPolicyOn ||
                        !canManageRetainLiveData
                      }
                      onChange={handleChange("historicalDataRetentionDays")}
                      error={!historicalDaysValid}
                      helperText={
                        !historicalDaysValid
                          ? "Must be a whole number of at least 1 day"
                          : ""
                      }
                    />
                    <TextField
                      variant="standard"
                      margin="normal"
                      fullWidth
                      type="number"
                      inputProps={{ min: 1 }}
                      label="Open/Reopened Vulnerabilities (Days)"
                      placeholder="Enter days"
                      value={form.liveDataRetentionDays}
                      disabled={
                        !client ||
                        retainLiveDataBusy ||
                        !retentionPolicyOn ||
                        !canManageRetainLiveData
                      }
                      onChange={handleChange("liveDataRetentionDays")}
                      error={!liveDaysValid}
                      helperText={
                        !liveDaysValid
                          ? "Must be a whole number of at least 1 day"
                          : ""
                      }
                    />
                    {canManageRetainLiveData && (
                      <Box mt={1}>
                        <Button
                          color={"primary"}
                          variant={"outlined"}
                          disabled={
                            !client ||
                            retainLiveDataBusy ||
                            !retentionValid ||
                            !retentionDirty
                          }
                          onClick={handleSaveRetentionSettings}
                        >
                          <Save />
                          Save Retention Settings
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Box>
              )}
            </Paper>
          </Grid>
          <Grid item md={8} sm={12} xs={12}>
            <Paper variant={"outlined"} className={classes.paper}>
              <Grid container>
                <Grid item style={{ flex: 1 }}>
                  <SectionGroupDescription
                    title={"Licenses"}
                    IconComponent={SecurityIcon}
                  />
                </Grid>
                <Grid item>
                  <Button
                    disabled={busy || !client || loadingLicense}
                    color={"primary"}
                    variant={"outlined"}
                    onClick={() => setOpenLicense(true)}
                  >
                    <HiveIcons.AddOutlined />
                    Add License
                  </Button>
                </Grid>
              </Grid>
              <Box my={3} />
              <Grid container>
                <ErrorBoundary>
                  <LicenseList client={client} />
                </ErrorBoundary>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>
      <CreateLicenseDialog
        open={openLicense}
        createEntity={createLicense}
        handleClose={() => setOpenLicense(false)}
        context={context.License}
        entityType={EntityType.License}
        clientId={id}
      />
    </SubmenuLayout>
  );
};

const mapStatetoProps = (state) => {
  return {
    context: state,
  };
};

export default connect(mapStatetoProps, { updateClient, createLicense })(
  ClientView
);
